import { useMemo } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import MediaAPI from "../api/MediaAPI";
import OfferAPI from "../api/OfferAPI";
import SmartlistAPI from "../api/SmartlistAPI";

import type { QueryKey } from "@tanstack/react-query";

import type { ItemWithPicture } from "../components/formTranslations/FormPictures";
import type { PictureKeys } from "../constants/ratio";
import type { Picture } from "../types";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TAG_TYPE = {
  CASTINGS: "castings",
  MEDIAS: "medias",
  OFFERS: "offers",
  SMARTLISTS: "smartlists",
} as const;

export type TagType = (typeof TAG_TYPE)[keyof typeof TAG_TYPE];

interface UsePictureTagsProps<T> {
  itemType: TagType;
  itemData: T;
  onItemUpdate?: (item: T) => void;
}

export interface TagState {
  tagId?: string;
  isDeleteLoading?: boolean;
  isDeleteSuccess?: boolean;
  isUpdateLoading?: boolean;
  isUpdateSuccess?: boolean;
}

export type TagDeleteFunction = (args: {
  tagId: string;
  translationId: string;
}) => void;

export interface TagUpdateArgs {
  tagId: string;
  tagValue: string;
  translationId: string;
  ratioId: PictureKeys;
}

export type TagUpdateFunction = (args: TagUpdateArgs) => void;

interface UsePictureTagsReturn {
  handleTagDelete: TagDeleteFunction;
  handleTagUpdate: TagUpdateFunction;
  tagState: TagState;
}

export default function usePictureTags<T extends ItemWithPicture>({
  itemType,
  itemData,
  onItemUpdate,
}: UsePictureTagsProps<T>): UsePictureTagsReturn {
  const queryClient = useQueryClient();

  const { t } = useTranslation("editor-pictures");
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const getDeleteMutationFn = async (tagId: string): Promise<unknown> => {
    switch (itemType) {
      case "castings":
      case "medias":
        return MediaAPI.deletePictureTag(tagId);

      case "offers":
      case "smartlists":
        return SmartlistAPI.deletePictureTag({ tagId, projectSlug });

      default:
        throw new Error(`Unknown type: ${itemType}`);
    }
  };

  const getUpdateMutationFn = async ({
    tagId,
    tagValue,
    translationId,
    ratioId,
  }: TagUpdateArgs): Promise<unknown> => {
    switch (itemType) {
      case "castings":
      case "medias":
        return MediaAPI.updatePictureTag({
          tagId,
          tagValue,
          translationId,
          ratioId,
          itemType,
        });
        break;

      case "offers":
      case "smartlists":
        return SmartlistAPI.updatePictureTag({
          tagId,
          tagValue,
          translationId,
          ratioId,
          projectSlug,
        });

        break;

      default:
        throw new Error(`Unknown type: ${itemType}`);
    }
  };

  const queryKey = useMemo((): QueryKey => {
    switch (itemType) {
      case "castings":
        return MediaAPI.generateQuery({
          fn: MediaAPI.getCasting,
          params: [itemData.uuid],
        }).queryKey;

      case "medias":
        return MediaAPI.generateQuery({
          fn: MediaAPI.getMedia,
          params: [itemData.uuid],
        }).queryKey;

      case "offers":
        return OfferAPI.generateQuery({
          fn: OfferAPI.getOffer,
          params: [{ offerId: itemData.uuid, projectSlug }],
        }).queryKey;

      case "smartlists":
        return SmartlistAPI.generateQuery({
          fn: SmartlistAPI.getSingleSmartlist,
          params: [{ smartlistId: itemData.uuid, projectSlug }],
        }).queryKey;

      default:
        throw new Error(`Unknown type: ${itemType}`);
    }
  }, [itemData.uuid, itemType, projectSlug]);

  const updateItemState = ({
    action,
    tagId,
    tagValue,
    translationId,
  }: {
    action: "delete" | "update";
    tagId: string;
    tagValue?: string;
    translationId: string;
  }): void => {
    const updatedItem = {
      ...itemData,
      translations: itemData.translations.map((translation) => {
        if (translation.uuid === translationId) {
          if (!translation.picture) return translation;

          const updatedPicture = Object.keys(translation.picture).reduce(
            (acc, currentPictureKey) => {
              const picture =
                translation.picture?.[currentPictureKey as PictureKeys];

              if (!picture) return acc;

              const updatedTags =
                action === "delete"
                  ? picture.tags.filter((tag) => tag.uuid !== tagId)
                  : picture.tags.map((tag) => {
                      if (tag.uuid === tagId) {
                        return {
                          ...tag,
                          label: tagValue,
                        };
                      }
                      return tag;
                    });

              return {
                ...acc,
                [currentPictureKey]: {
                  ...picture,
                  tags: updatedTags,
                },
              };
            },
            {} as Picture,
          );

          return {
            ...translation,
            picture: updatedPicture,
          };
        }
        return translation;
      }),
    };

    onItemUpdate?.(updatedItem);
  };

  const deleteTag = useMutation<
    unknown,
    unknown,
    {
      tagId: string;
      translationId: string;
    }
  >({
    mutationFn: async ({ tagId }) => getDeleteMutationFn(tagId),
    onSuccess: (_, { tagId, translationId }) => {
      queryClient.invalidateQueries(queryKey);
      toast.success(t("deleteTagSuccess"));

      updateItemState({
        action: "delete",
        tagId,
        translationId,
      });
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleTagDelete = (args: {
    tagId: string;
    translationId: string;
  }): void => {
    deleteTag.mutate(args);
  };

  const updateTag = useMutation<unknown, unknown, TagUpdateArgs>({
    mutationFn: async ({ tagId, tagValue, translationId, ratioId }) =>
      getUpdateMutationFn({ tagId, tagValue, translationId, ratioId }),
    onSuccess: (_, { tagId, tagValue, translationId }) => {
      queryClient.invalidateQueries(queryKey);

      updateItemState({
        action: "update",
        tagId,
        tagValue,
        translationId,
      });

      toast.success(t("updateTagSuccess"));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleTagUpdate = (args: TagUpdateArgs): void => {
    updateTag.mutate(args);
  };

  const tagState = {
    tagId: deleteTag.variables?.tagId,
    isDeleteLoading: deleteTag.isLoading,
    isDeleteSuccess: deleteTag.isSuccess,
    isUpdateLoading: updateTag.isLoading,
    isUpdateSuccess: updateTag.isSuccess,
  };

  return {
    handleTagDelete,
    handleTagUpdate,
    tagState,
  };
}
